import {
    createFileRoute,
    Link,
    Outlet,
    redirect,
    useBlocker,
    useNavigate,
    useRouterState,
} from '@tanstack/react-router'
import React, { useEffect, useState } from 'react'
import { Button, Form, notification } from 'antd'
import { MessageOutlined } from '@ant-design/icons'
import { isAxiosError } from 'axios'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { CampaignSettingsShell } from '../campaign/components/settings/campaign-settings-shell'
import { CampaignSettingsActions } from '../campaign/components/settings/campaign-settings-actions'
import { CampaignSettingsPanel } from '../campaign/components/settings/campaign-settings-panel'
import { CampaignSettingsHeaderActions } from '../campaign/components/settings/campaign-settings-header-actions'
import {
    campaignSettingsNavigation,
    campaignSettingsPanelKey,
    campaignSettingsPanels,
    campaignSettingsPath,
} from '../campaign/components/settings/campaign-settings-panels'
import {
    customAttributeRows,
    notifyCampaignUpdated,
    toUpdateInput,
} from '../campaign/components/settings/campaign-settings-utils'
import { CampaignSettingsFormValues } from '../campaign/components/settings/types'
import { Campaign } from '../campaign/campaign'
import { CampaignTryOut } from '../campaign/components/campaign-try-out'
import { ViewCampaign } from '../campaign/components/view-campaign'
import { useCampaign, useUpdateCampaign } from '../campaign/hooks'
import { Container } from '../components/layout/container'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { useTitle } from '../hooks/use-title'
import { useAuth } from '../auth'
import {
    INDEX_CLIENT,
    INDEX_MESSAGE_TEMPLATE,
    INDEX_PHONE_NUMBERS,
    INDEX_WHATSAPP_BUSINESS_ACCOUNT,
    UPDATE_CAMPAIGN,
} from '../user/permission'
import { addValidationOnError, onErrorNotifyAboutValidationError } from '../util'

export const Route = createFileRoute('/_auth/_main/campaigns/$campaignId/settings')({
    beforeLoad: ({ context }) => {
        if (!context.auth.can(UPDATE_CAMPAIGN)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: CampaignSettingsLayout,
    validateSearch: z.object({
        bulk: z.boolean().optional(),
        source: z.enum(['public', 'facebook']).optional().catch(undefined),
        test: z.boolean().optional(),
    }),
})

function CampaignSettingsLayout() {
    const { campaignId } = Route.useParams()
    const { bulk, test } = Route.useSearch()
    const { t } = useTranslation()
    const { can } = useAuth()
    const navigate = useNavigate()
    const pathname = useRouterState({ select: (state) => state.location.pathname })

    const [form] = Form.useForm<CampaignSettingsFormValues>()

    const [isDirty, setIsDirty] = useState(false)
    const [showTryOut, setShowTryOut] = useState(false)
    const [testCampaign, setTestCampaign] = useState<Campaign>()

    const campaign = useCampaign(campaignId)
    const updateCampaign = useUpdateCampaign(campaignId)

    useTitle(`${campaign.data?.title} - ${t('Settings')}`)

    useEffect(() => {
        if (!test || !campaign.data) return

        setTestCampaign({ ...campaign.data, ...form.getFieldsValue(true) })
        setShowTryOut(true)
        void navigate({
            to: '.',
            search: (previous) => ({ ...previous, test: undefined }),
            replace: true,
            resetScroll: false,
        })
    }, [test, campaign.data, form, navigate])

    useBlocker({
        shouldBlockFn: ({ next }) => {
            // Allow moving between settings sections — the form is shared and edits are preserved.
            if (!isDirty || next.pathname.startsWith(`/campaigns/${campaignId}/settings`)) {
                return false
            }

            return !window.confirm(t('You have unsaved changes. Are you sure you want to leave?'))
        },
        enableBeforeUnload: () => isDirty,
    })

    if (campaign.isPending || campaign.isError) {
        return null
    }

    const navigationItems = campaignSettingsNavigation(t, campaignId, {
        whatsapp: can([INDEX_WHATSAPP_BUSINESS_ACCOUNT, INDEX_PHONE_NUMBERS, INDEX_MESSAGE_TEMPLATE]),
        clients: can(INDEX_CLIENT),
    })
    const panelKey = campaignSettingsPanelKey(pathname, campaignId)
    const activePanel = campaignSettingsPanels(t, campaign.data.type)[panelKey]

    const openTryOut = () => {
        setTestCampaign({ ...campaign.data, ...form.getFieldsValue(true) })
        setShowTryOut(true)
    }

    const handleSubmit = (values: CampaignSettingsFormValues) => {
        updateCampaign
            .mutateAsync(toUpdateInput(values))
            .then(() => {
                setIsDirty(false)
                notifyCampaignUpdated(t)
            })
            .catch((error) => {
                if (isAxiosError(error) && error.response?.status === 422) {
                    addValidationOnError(form)(error)
                    onErrorNotifyAboutValidationError(error)
                    return
                }

                notification.error({
                    message: t('Could not save {{name}}', { name: t('Campaign') }),
                })
            })
    }

    return (
        <ViewCampaign
            campaign={campaign.data}
            allowWithoutSubscription
            backToBoard
            pageHeader={{
                wrap: true,
                className:
                    'sticky top-0 z-10 -mx-6 -mt-6 min-h-[var(--campaign-settings-header-height)] border-b border-hairline bg-white px-6 pb-4 pt-6',
            }}
            headerActions={
                <Button
                    icon={<MessageOutlined />}
                    onClick={openTryOut}
                >
                    {t('Test conversation')}
                </Button>
            }
            candidateImportModal={{
                open: bulk,
                onCancel: () => {
                    navigate({
                        to: '.',
                        search: {},
                    })
                },
            }}
        >
            <PortaledBreadcrumb
                items={[
                    {
                        title: <Link to="/campaigns">{t('Campaigns')}</Link>,
                    },
                    {
                        title: (
                            <Link
                                to="/campaigns/$campaignId/board"
                                params={{ campaignId }}
                            >
                                {campaign.data.title}
                            </Link>
                        ),
                    },
                    {
                        title: t('Settings'),
                    },
                ]}
            />
            <Container>
                <CampaignSettingsShell
                    items={navigationItems}
                    activeKey={campaignSettingsPath(campaignId, activePanel.navigationKey)}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{
                            ...campaign.data,
                            customAttributeRows: customAttributeRows(campaign.data.customAttributes || {}),
                        }}
                        onValuesChange={() => setIsDirty(true)}
                        onFinish={handleSubmit}
                        className="w-full min-w-0"
                    >
                        <CampaignSettingsPanel
                            title={activePanel.title}
                            description={activePanel.description}
                            headerActions={
                                <CampaignSettingsHeaderActions
                                    campaignId={campaignId}
                                    panelKey={panelKey}
                                />
                            }
                            actions={
                                activePanel.canSave && (
                                    <CampaignSettingsActions
                                        campaignId={campaignId}
                                        isSubmitting={updateCampaign.isPending}
                                    />
                                )
                            }
                        >
                            <Outlet />
                        </CampaignSettingsPanel>
                    </Form>
                </CampaignSettingsShell>
            </Container>
            {testCampaign && (
                <CampaignTryOut
                    campaign={testCampaign}
                    open={showTryOut}
                    title={t('Test conversation')}
                    onCancel={() => setShowTryOut(false)}
                />
            )}
        </ViewCampaign>
    )
}
